import { get, cloneDeep, set } from 'lodash';
import { DEFAULT_TIMEFRAME_ABSOLUTE_HOUR_VALUE, DEFAULT_TIMEFRAME_RELATIVE_HOUR_VALUE, TIMEFRAME_FILTER_TYPES } from '@config/Consts';

const setSettings = (state, settings) => {
  state.userSettings = cloneDeep(settings);
};

const setMetricsConfiguration = (state, metricsConfiguration) => {
  state.accountSettings.metricsConfiguration.data = metricsConfiguration;
};

const setMetricsConfigurationRevision = (state, revision) => {
  if (revision) {
    state.accountSettings.metricsConfiguration.revision = revision;
  }
};

const setTimeframe = (state, timeFrame) => {
  const type = get(timeFrame, 'type') || TIMEFRAME_FILTER_TYPES.RELATIVE;
  const relativeHour = get(timeFrame, 'relativeHour') || DEFAULT_TIMEFRAME_RELATIVE_HOUR_VALUE;
  const absoluteHour = get(timeFrame, 'absoluteHour') || DEFAULT_TIMEFRAME_ABSOLUTE_HOUR_VALUE;
  state.userSettings.widget_settings.global_filters.timeframe =
    { type, relativeHour, absoluteHour };
};

const setAgentGroups = (state, agentGroups) => {
  if (agentGroups) {
    state.accountData.agentGroups.revision = agentGroups.revision;
    state.accountData.agentGroups.data = agentGroups.data;
  }
};

const setAgentGroupsFilter = (state, agentGroupsFilter) => {
  state.userSettings.widget_settings.global_filters.groups = agentGroupsFilter;
};

const setSkillsFilter = (state, skillsFilter) => {
  state.userSettings.widget_settings.global_filters.skills = skillsFilter;
};

const setSkills = (state, skills) => {
  if (skills) {
    state.accountData.skills.revision = skills.revision;
    state.accountData.skills.data = skills.data;
  }
};

const setUserManagedData = (state, userManagedData) => {
  if (userManagedData) {
    state.accountData.userManaged.revision = userManagedData.revision;
    state.accountData.userManaged.data = userManagedData.data;
  }
};

const setUsers = (state, users) => {
  if (users) {
    state.accountData.users.revision = users.revision;
    state.accountData.users.data = users.data;
  }
};

const setStatusReasons = (state, statusReasons) => {
  if (statusReasons) {
    state.accountData.statusReasons.revision = statusReasons.revision;
    state.accountData.statusReasons.data = statusReasons.data;
  }
};

const setAgentGroupsUpdateRequired = (state, dataUpdateRequired) => {
  state.accountData.agentGroups.dataUpdateRequired = dataUpdateRequired;
};

const setBlockConsumerName = (state, blockConsumerName) => {
  state.accountSettings.blockConsumerName = blockConsumerName;
};

const setCanConfigureManagerWorkspace = (state, canConfigureManagerWorkspace) => {
  state.accountSettings.canConfigureManagerWorkspace = canConfigureManagerWorkspace;
};

const setNewManagerApiUsage = (state, useNewManagerApi) => {
  state.useNewManagerApi = useNewManagerApi;
};

const setFetchResponseMetricsState = (state, fetchResponseMetrics) => {
  state.fetchResponseMetrics = fetchResponseMetrics;
};

const setEnableIntentsState = (state, enableIntents) => {
  state.enableIntents = enableIntents;
};

const setEnableIntentsFilterState = (state, enableIntentsFilter) => {
  state.enableIntentsFilter = enableIntentsFilter;
};

const setAmwVersion = (state, amwVersion) => {
  state.amwVersion = amwVersion;
};

const setMetricsConfigurationVersion = (state, version) => {
  state.accountSettings.metricsConfiguration.version = version;
};

const setGlobalFiltersUpdating = (state, data) => {
  if (data) {
    const globalFiltersUpdatingState = state.globalFiltersUpdating;
    const widgetName = data.widgetName;
    if (widgetName) {
      globalFiltersUpdatingState[widgetName] = data.value;
    } else {
      Object.keys(globalFiltersUpdatingState).forEach((key) => {
        globalFiltersUpdatingState[key] = data.value;
      });
    }
  }
};

const setAccountDataStatus = (state, status) => {
  state.accountData.dataStatus = status;
};

const setAccountDataSubscriptionStatus = (state, status) => {
  state.accountData.subscriptionsStatus = status;
};

const setWidgetsSubscriptionsStatus = (state, status) => {
  state.widgetsSubscriptionsStatus = status;
};

const setWorkspacePartialWidgets = (state) => {
  state.workspacePartialWidgets = true;
};

const setUseBotsGraphMock = (state, useBotsGraphMock) => {
  state.useBotsGraphMock = useBotsGraphMock;
};

const setBotsGraphMock = (state, botsGraphMock) => {
  state.botsGraphMock = botsGraphMock;
};

const setIsModuleActive = (state, isModuleActive) => {
  state.isModuleActive = isModuleActive;
};

const setPresetName = (state, presetName) => {
  state.userSettings.widget_settings.preset.currentPreset
    = { name: presetName };
};

const updateExistingPreset = (state, presetValues) => {
  const allPresets = state.userSettings.widget_settings.preset.presets;
  const index = allPresets.findIndex(item => item.name === presetValues.name);
  if (index > -1) {
    allPresets.splice(index, 1, presetValues);
  }
};

const addNewPreset = (state, presetValues) => {
  setPresetName(state, presetValues.name);
  state.userSettings.widget_settings.preset.presets.push(presetValues);
};

const removePreset = (state, presetName) => {
  const allPresets = state.userSettings.widget_settings.preset.presets;
  const index = allPresets.findIndex(item => item.name === presetName);
  allPresets.splice(index, 1);
};

const setGlobalPresetValues = (state, preset) => {
  setPresetName(state, preset.name);
  setSkillsFilter(state, preset.widgets.global.skills);
  setAgentGroupsFilter(state, preset.widgets.global.groups);
  setTimeframe(state, preset.widgets.global.timeframe);
};

const setPresetsValidatedOnFirstLoad = (state) => {
  state.presetsValidatedOnFirstLoad = true;
};

const setShowBotGraph = (state, showBotGraph) => {
  state.showBotGraph = showBotGraph;
};

const setDialogAlertItems = (state, dialogAlertItems) => {
  state.dialogAlertItems = dialogAlertItems;
};

const resetDialogAlertItems = (state) => {
  state.dialogAlertItems = {};
};

const setAlertData = (state, alertData) => {
  state.alertData = alertData;
};

const updateWidgetExpandedH = (state, config) => {
  const widgetSettingsArray = get(state.userSettings, 'widget_settings.settings');
  const widgetSettings = widgetSettingsArray.find(item => item.name === config.widgetName);
  set(widgetSettings.config, 'isCollapsed', config.isCollapsed);
  set(widgetSettings.config, 'expandedH', config.expandedH);
  widgetSettings.config.originalSize.h = config.isCollapsed ? config.h : config.expandedH;
};

const setSelectedTabId = (state, tabId) => {
  state.userSettings.widget_settings.selectedTabId = tabId;
};

const setOriginalSize = (state, widgetName) => {
  const widgetSettingsArray = get(state.userSettings, 'widget_settings.settings');
  const widgetSettings = widgetSettingsArray.find(item => item.name === widgetName);
  widgetSettings.config.originalSize = {
    h: widgetSettings.config.h,
    w: widgetSettings.config.w,
    x: widgetSettings.config.x,
    y: widgetSettings.config.y };
};

const resetGridItemSize = (state, options) => {
  const widgetSettingsArray = get(state.userSettings, 'widget_settings.settings');
  const widgetSettings = widgetSettingsArray.find(item => item.name === options.id);
  const widgetSize = { x: options.x, y: options.y, w: options.w, h: options.h };
  widgetSettings.config = { ...widgetSettings.config, ...widgetSize };
};

export default {
  setSettings,
  setTimeframe,
  setAgentGroups,
  setAgentGroupsFilter,
  setSkillsFilter,
  setGlobalFiltersUpdating,
  setSkills,
  setUserManagedData,
  setUsers,
  setStatusReasons,
  setAgentGroupsUpdateRequired,
  setBlockConsumerName,
  setCanConfigureManagerWorkspace,
  setNewManagerApiUsage,
  setFetchResponseMetricsState,
  setEnableIntentsState,
  setEnableIntentsFilterState,
  setAmwVersion,
  setAccountDataStatus,
  setAccountDataSubscriptionStatus,
  setWidgetsSubscriptionsStatus,
  setMetricsConfiguration,
  setMetricsConfigurationRevision,
  setMetricsConfigurationVersion,
  setWorkspacePartialWidgets,
  setIsModuleActive,
  setUseBotsGraphMock,
  setBotsGraphMock,
  setPresetName,
  setGlobalPresetValues,
  updateExistingPreset,
  addNewPreset,
  removePreset,
  setPresetsValidatedOnFirstLoad,
  setShowBotGraph,
  setDialogAlertItems,
  setAlertData,
  resetDialogAlertItems,
  updateWidgetExpandedH,
  setSelectedTabId,
  setOriginalSize,
  resetGridItemSize,
};

